import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { Button } from "./ui/button";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Hampers" },
  { to: "/chocolates", label: "Chocolates" },
  { to: "/gifts", label: "Gifts" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b border-[#e8e2da] bg-white/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="text-2xl font-serif font-semibold tracking-tight text-[#2a2928]">
            balloon<span className="text-[#d08c8c]">.</span>bites
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#d08c8c]" : "text-[#2a2928] hover:text-[#d08c8c]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-cart-button"
            onClick={() => nav("/cart")}
            className="relative rounded-full p-2 text-[#2a2928] transition-colors hover:bg-[#f4ede6]"
            aria-label="Cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#d08c8c] px-1 text-[10px] font-semibold text-white"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="hidden items-center gap-1 md:flex">
              {isAdmin && (
                <Button
                  data-testid="nav-admin-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => nav("/admin")}
                  className="rounded-full"
                >
                  <LayoutDashboard className="mr-1.5 h-4 w-4" /> Admin
                </Button>
              )}
              <Button
                data-testid="nav-account-btn"
                variant="ghost"
                size="sm"
                onClick={() => nav("/account")}
                className="rounded-full"
              >
                <User className="mr-1.5 h-4 w-4" /> {user.name.split(" ")[0]}
              </Button>
              <Button
                data-testid="nav-logout-btn"
                variant="ghost"
                size="sm"
                onClick={() => {
                  logout();
                  nav("/");
                }}
                className="rounded-full"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              data-testid="nav-login-btn"
              onClick={() => nav("/login")}
              className="hidden rounded-full bg-[#d08c8c] px-5 text-white hover:bg-[#b87a7a] md:inline-flex"
              size="sm"
            >
              Sign In
            </Button>
          )}

          <button
            data-testid="nav-mobile-toggle"
            className="rounded-full p-2 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[#e8e2da] bg-white md:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="rounded-lg px-3 py-2 text-sm font-medium text-[#2a2928] hover:bg-[#f4ede6]"
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                {isAdmin && (
                  <button
                    className="rounded-lg px-3 py-2 text-left text-sm font-medium"
                    onClick={() => {
                      setOpen(false);
                      nav("/admin");
                    }}
                  >
                    Admin
                  </button>
                )}
                <button
                  className="rounded-lg px-3 py-2 text-left text-sm font-medium"
                  onClick={() => {
                    setOpen(false);
                    nav("/account");
                  }}
                >
                  My Account
                </button>
                <button
                  className="rounded-lg px-3 py-2 text-left text-sm font-medium text-[#d08c8c]"
                  onClick={() => {
                    setOpen(false);
                    logout();
                    nav("/");
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                data-testid="nav-mobile-login-btn"
                className="mt-1 rounded-full bg-[#d08c8c] px-5 py-2 text-sm font-medium text-white"
                onClick={() => {
                  setOpen(false);
                  nav("/login");
                }}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
